import React, { useState } from 'react';
import { Modal, Textarea, Input, Select, IOption } from 'shared-components';

import { ENROLY_AGENT_ROLES, UNI_ROLES, AGENT_ROLES } from '../../../../store/auth/types';
import { DocAccessType, IFollowUpArea, IMessage } from 'api-paths-cas';
import { Button, ButtonGroup, Checkbox } from '@chakra-ui/react';

export interface IDispatchProps {
  onClose: () => void;
  onSubmitMessage: (
    isFinancialDoc: boolean,
    isFollowUpDoc: boolean,
    message: IMessage,
    documentName: string,
    documentDescription: string,
    userAccess?: DocAccessType,
    followUpArea?: string,
  ) => void;
}

export interface IProps {
  userName: string;
  displayName: string;
  isFinancialDoc: boolean;
  isFollowUpDoc: boolean;
  isBeforeArrivalAreaHidden: boolean;
  isAfterArrivalAreaHidden: boolean;
  role: string;
  hasAgent: boolean;
  followUpArea?: IFollowUpArea;
}

export enum CustomDocType {
  REQUIRED = 'REQUIRED',
  STAFF_ONLY = 'STAFF_ONLY',
  AGENT_ONLY = 'AGENT_ONLY',
}

type ICustomProps = IProps & IDispatchProps;

const CustomRequestDocumentModal = (props: ICustomProps) => {
  const initialMessage: IMessage = {
    message: '',
    isSendToStudent: true,
    messageType: 'note',
    createdAt: 0,
    author: '',
    authorName: '',
    messageFrom: 'uni',
    messageTo: 'student',
  };
  const [message, setMessage] = useState(initialMessage);
  const [isSendToStudent, setIsSendToStudent] = useState(true);
  const [isSendToAgent, setIsSendToAgent] = useState(true);
  const [isFinancialDoc, _setIsFinancialDoc] = useState(props.isFinancialDoc);
  const [documentName, setDocumentName] = useState('');
  const [documentType, setDocumentType] = useState('');

  const docTypeOptions: IOption[] = [
    { label: 'Request a document from the student/agent', value: CustomDocType.REQUIRED },
    ...(props.hasAgent ? [{ label: 'Request a document from the agent only', value: CustomDocType.AGENT_ONLY }] : []),
    { label: 'Create a document visible to staff only', value: CustomDocType.STAFF_ONLY },
  ];

  const submitMessage = () => {
    if (!message) {
      return;
    }
    const author = props.userName || 'Anonymous';
    const getMessageTo = (messageFrom = 'uni') => {
      if (documentType === CustomDocType.REQUIRED && isSendToStudent) {
        return 'student';
      }
      if (documentType === CustomDocType.AGENT_ONLY && isSendToAgent) {
        return 'agent';
      }
      return messageFrom;
    };

    const getMessageFrom = () => {
      if (ENROLY_AGENT_ROLES.includes(props.role)) {
        return 'enroly';
      }
      return 'uni';
    };
    const messageFrom = getMessageFrom();
    const newMessage: IMessage = {
      message: `Custom Request Description: ${message.message}`,
      createdAt: Date.now(),
      isSendToStudent: documentType === CustomDocType.REQUIRED && isSendToStudent,
      messageType:
        documentType === CustomDocType.AGENT_ONLY && isSendToAgent
          ? 'agentUni'
          : isSendToStudent
            ? 'note'
            : 'personalNote',
      author,
      authorName: props.displayName || author,
      messageFrom,
      messageTo: getMessageTo(messageFrom),
      ...(UNI_ROLES.includes(props.role) ? { uniRead: true } : {}),
      ...(ENROLY_AGENT_ROLES.includes(props.role) ? { adminRead: true } : {}),
      ...(AGENT_ROLES.includes(props.role) ? { agentRead: true } : {}),
    };

    const getUserAccess = () => {
      switch (documentType) {
        case CustomDocType.STAFF_ONLY:
          return DocAccessType.STAFF_ONLY;
        case CustomDocType.AGENT_ONLY:
          return DocAccessType.AGENT_ONLY;
        default:
          return undefined;
      }
    };

    props.onSubmitMessage(
      isFinancialDoc,
      props.isFollowUpDoc,
      newMessage,
      documentName,
      message.message,
      getUserAccess(),
      props.followUpArea,
    );

    props.onClose();
  };

  return (
    <Modal
      id={'custom-doc-request'}
      show={true}
      title={`Custom document request`}
      onClose={props.onClose}
      footer={
        <ButtonGroup size={'lg'} spacing={3}>
          <Button onClick={props.onClose}>Cancel</Button>

          <Button
            id={'custom-doc-submit'}
            key={'custom-doc-submit'}
            onClick={submitMessage}
            isDisabled={!message.message}
            variant={'solid'}
          >
            {documentType === CustomDocType.REQUIRED ? 'Request file' : 'Create file'}
          </Button>
        </ButtonGroup>
      }
    >
      <Select
        id={'custom-doc-type'}
        key={'custom-doc-type'}
        label={'Document type'}
        options={docTypeOptions}
        value={documentType}
        placeHolder="Select..."
        onChange={(evt: React.FormEvent<HTMLSelectElement>) => setDocumentType(evt.currentTarget.value)}
        mb={6}
      />

      {documentType && (
        <>
          <Input
            id={'custom-doc-name'}
            key={'custom-doc-name'}
            value={documentName}
            autoFocus={true}
            label={'Document name'}
            onChange={(evt: React.FormEvent<HTMLInputElement>) => setDocumentName(evt.currentTarget.value)}
            mb={6}
          />

          <Textarea
            id={'custom-doc-description'}
            key={'custom-doc-description'}
            label={'Custom document description/instructions'}
            value={message.message}
            onChange={(event: React.FormEvent<HTMLTextAreaElement>) =>
              setMessage({
                ...message,
                message: event.currentTarget.value,
              })
            }
            placeholder={'Type your instructions here...'}
          />

          {documentType === CustomDocType.REQUIRED && (
            <>
              <Checkbox
                id={'custom-doc-send-msg-to-student'}
                key={'custom-doc-send-msg-to-student'}
                isChecked={isSendToStudent}
                onChange={() => setIsSendToStudent(!isSendToStudent)}
                size={'lg'}
                mt={3}
              >
                Send this note to student
              </Checkbox>
            </>
          )}

          {documentType === CustomDocType.AGENT_ONLY && (
            <>
              <Checkbox
                id={'custom-doc-send-msg-to-agent'}
                key={'custom-doc-send-msg-to-agent'}
                isChecked={isSendToAgent}
                onChange={() => setIsSendToAgent(!isSendToAgent)}
                size={'lg'}
                mt={3}
              >
                Send this note to agent
              </Checkbox>
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default CustomRequestDocumentModal;
